// Import Packages
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Parser from 'html-react-parser';
import { useSetRecoilState, useRecoilValue } from 'recoil';

// Import Material UI
import { MenuItem, Select } from "@material-ui/core";
import FormControl from "@material-ui/core/FormControl";
import Dialog from '@material-ui/core/Dialog';
import { Edit, LocalOfferOutlined, Close } from '@material-ui/icons';
import Button from "@material-ui/core/Button";
import { Tooltip } from "@material-ui/core";

// Import Pages
import { alertState, personalizationState } from '../../../../recoil-store/recoil-state';
import GalleryCarousel from "./GalleryCarousel";
import Confirm from '../../../../components/DataTable/DeleteConfirm'
import RequiredLibraries from './requiredLibraries/RequiredLibraries';
import Slider from "../../../../components/Slider/Slider";

// Import Helpers
import { convertToDateFormat, convertToOnlyDateFormat } from '../../../../helpers/helper';

// Import Services
import IpaasService from '../../../../services/IpaasService/IpaasService';
import AutomService from '../../../../services/AutomService/AutomService';
import BotService from '../../../../services/BotService/BotService';

// Import Icons
import tagIcon from '../../../../assets/icons/tag-icon.svg';
import DetailsFullScreen from './DetailsFullScreen';

/**
 * @Description This function lets you return Details dialog component.
 * @Returns     Component
 * @Author      Ibrahim Akın | Abdullah Onur Yeşil
 */
function Details({ setOpen, bot }) {
    const { t } = useTranslation();
    const [selectedVersion, setSelectedVersion] = useState({});
    const [botDetail, setBotDetail] = useState({});
    const [downloadConfirm, setDownloadConfirm] = useState(false);
    const [activateConfirm, setActivateConfirm] = useState(false);
    const [formReady, setFormReady] = useState(false);
    const [openLibraryList, setOpenLibraryList] = useState(false);

    const [detail, setDetail] = useState('description');
    const [openFullScreen, setOpenFullScreen] = useState(false);
    const setAlert = useSetRecoilState(alertState);
    const personalization = useRecoilValue(personalizationState);

    const label = 'CONTENT.BOT-DETAILS.';
    const messageLabel = 'MESSAGES.STORE.';

    let bpn = bot.business_process_name?.split(',');
    let cn = bot.categories_name?.split(',');

    useEffect(() => {
        async function getBotDetail() {
            const response = await BotService.getBotVersions(bot.bot_id);
            if (response && response.status && response.data) {
                response.data.bot_details = response.data.bot_details[0];
                setBotDetail(response.data);
                setSelectedVersion(response.data.bot_versions[response.data.bot_versions.length - 1]);
                setFormReady(true);
            } else {
                setAlert(alert => ({
                    ...alert,
                    show: true,
                    message: t(response && response.message ? messageLabel + response.message : 'GENERAL.NETWORK-ERROR'),
                    severity: 'error'
                }))
                handleClose();
            }
        }

        getBotDetail();
    }, []);

    const handleClose = () => {
        setOpen(false);
    };

    const handleClick = (e) => {
        let target = e.target;
        for (const el of target.parentNode.childNodes) {
            el.classList.remove('bot-tabactive');
        }
        target.classList.add('bot-tabactive');
        setDetail(target.value);
    }
    const handleFullScreen = (source) => {
        setOpenFullScreen(source)
    }

    const reList = () => {
        let copyBotDetail = JSON.parse(JSON.stringify(botDetail));
        for (const version of copyBotDetail.bot_versions) {
            if (version.id === selectedVersion.id) {
                version.downloaded = true;
                version.is_active_version = true;
                setSelectedVersion(version);
            } else {
                // version.downloaded = false;
                version.is_active_version = false;
            }
        }
        setBotDetail(copyBotDetail);
    }

    const downloadBot = async (callback) => {
        // TODO handle download, increase download count
        const response = await AutomService.downloadAutom(bot.bot_id, selectedVersion.id);
        let message = '';
        if (response) {
            if (response.status) {
                message = t(messageLabel + response.message);
                callback({ status: true, message });
                reList();

                // const i = copyBotDetail.bot_versions.findIndex(x => x.id === selectedVersion.id);
                // if (i !== -1) {
                //     copyBotDetail.bot_versions[i].is_installed = true;
                //     setBotDetail(copyBotDetail);
                //     setSelectedVersion(copyBotDetail.bot_versions[i]);
                // }
            } else {
                message = t(response.message.includes('GENERAL') ? response.message : messageLabel + response.message);
                callback({ status: false, message });
                setAlert(alert => ({
                    ...alert,
                    show: true,
                    message,
                    severity: 'error'
                }))
            }
        } else {
            message = t('GENERAL.NETWORK-ERROR');
            callback({ status: false, message });
            setAlert(alert => ({ ...alert, show: true, message, severity: 'error' }))
        }
    };

    const changeActiveVersion = async () => {
        const response = await AutomService.setActiveVersion({
            name: bot.bot_name,
            vendor: bot.vendor, version: selectedVersion.version,
            description: bot.short_description
        });

        if (response) {
            if (response.status) {
                setAlert(alert => ({
                    ...alert,
                    show: true,
                    message: t(messageLabel + "SUCCESSFULLY-ACTIVATED"),
                    severity: 'success'
                }))
                reList();
            } else {
                setAlert(alert => ({
                    ...alert,
                    show: true,
                    message: t(response.message.includes('GENERAL') ? response.message : messageLabel + response.message),
                    severity: 'error'
                }))
            }
        } else {
            setAlert(alert => ({ ...alert, show: true, message: t('GENERAL.NETWORK-ERROR'), severity: 'error' }))
        }
    }

    return (
        <Dialog onClose={handleClose} open={true} maxWidth="md" fullWidth={true}>
            {formReady && <div className="bot-dialog">
                <div className="bot-dialog-close"><Close fontSize="small" onClick={handleClose} /></div>
                <div className="bot-dialog-body">
                    <div className="bot-details-section">
                        <div className="bot-name">{bot.bot_name}</div>
                        <div style={{ marginTop: '10px' }} className='badge-area'>
                            <div className="badge-container">
                                {bpn?.map((item, itemI) => {
                                    if (itemI < 3) {
                                        return <div className={`badge-item red ${itemI > 0 ? 'm-l-30' : ''}`} key={itemI}>{item}</div>
                                    }
                                })}
                                {bpn?.length > 3 ? <Tooltip title={bpn?.slice(3, bpn?.length).join(', ')}><span
                                    className="badge-plus">+{bpn?.length - 3}</span></Tooltip> : null}
                            </div>
                            <div className="badge-container m-l-30">
                                {cn?.map((item, itemI) => {
                                    if (itemI < 1) {
                                        return <div className="badge-item blue" key={itemI}>{item}</div>
                                    }
                                })}
                                {cn?.length > 1 ? <Tooltip title={cn?.slice(1, cn?.length).join(', ')}><span
                                    className="badge-plus">+{cn?.length - 1}</span></Tooltip> : null}
                            </div>
                        </div>
                        {/* <div className="bot-footer">
                            <div className="bot-badge blue">{bot.automation_type_name}</div>
                            {bpn?.map((item, itemI) => {
                                return <div className="bot-badge red">{item}</div>
                            })}
                            {cn?.map((item, itemI) => {
                                return <div className="bot-badge orange">{item}</div>
                            })}

                            <div className="bot-badge gray">{bot.security_level}</div>
                        </div> */}
                        <div className='slider-area'>
                            {/* <GalleryCarousel images={botDetail?.bot_images} /> */}
                            <Slider images={botDetail?.bot_images} handleFullScreen={handleFullScreen} />
                        </div>
                        <div
                            className="bot-description">{Parser(bot.short_description ? bot.short_description : '')}</div>
                        {/* <div className="bot-details">The ServiceDesk Plus WhatsApp Integration bot provides to create a new service request to ServiceDesk Plus via Whatsapp chat. When the end-user texts to WhatsApp Business Number of your company, this bot determines the user from the</div> */}
                        <div className="bot-tabs">
                            <div />
                            <button className="bot-tabactive" value="description" onClick={handleClick}>
                                {t(label + 'DESCRIPTION')}
                            </button>
                            <button value="benefits" onClick={handleClick}>
                                {t(label + 'BENEFIT')}
                            </button>
                            <div />
                            <button value="setup" onClick={handleClick}>
                                {t(label + 'SET-UP')}
                            </button>
                            <div />
                            <button style={{ marginRight: '5px' }} value="guide" onClick={handleClick}>
                                {t(label + 'GUIDE')}
                            </button>
                        </div>
                        <ul className="bot-card-details">
                            {
                                detail === 'benefits' ? Parser(botDetail?.bot_details?.benefit ? botDetail.bot_details.benefit : '') :
                                    detail === 'guide' ? Parser(botDetail?.bot_details?.guide ? botDetail.bot_details.guide : '') :
                                        detail === 'description' ? Parser(botDetail?.bot_details?.description ? botDetail.bot_details.description : '') :
                                            detail === 'setup' ? Parser(botDetail?.bot_details?.configuration ? botDetail.bot_details.configuration : '') : null
                                // detail === 'gallery' ? <Gallery images={bot?.images}/> : null
                            }
                        </ul>
                    </div>
                    <div className="bot-tags-section">
                        <div>
                            <div className="version-title">Versions</div>
                            <FormControl style={{ marginRight: 10, width: '116px' }}>
                                <Select
                                    id="demo-simple-select-outlined"
                                    value={selectedVersion}
                                    size="small"
                                    onChange={(e) => setSelectedVersion(e.target.value)}
                                    variant="outlined"
                                    displayEmpty
                                    inputProps={{ "aria-label": "Without label" }}
                                    style={{
                                        height: '27px',
                                        fontWeight: '600',
                                        fontSize: '11px',
                                        lineHeight: '14px',
                                        color: '#AFAFAF',
                                        background: '#FAFAFA',
                                        border: '0.5px solid #fefefe',
                                        borderRadius: '2px'
                                    }}
                                >
                                    {botDetail && botDetail.bot_versions &&
                                        botDetail.bot_versions.map((item, index) =>
                                            item.is_active_version ? (
                                                <MenuItem key={index} value={item}>
                                                    {`${item.version} (active)`}
                                                </MenuItem>
                                            ) : (
                                                <MenuItem key={index} value={item}>
                                                    {item.version}
                                                </MenuItem>
                                            )
                                        )}
                                </Select>
                            </FormControl>
                            {
                                selectedVersion.version && (
                                    !selectedVersion.downloaded ?
                                        (
                                            <Button
                                                className='detail-autom-button get-autom'
                                                variant="contained"
                                                disabled={false}
                                                onClick={() => setDownloadConfirm(true)}
                                            >
                                                {t(label + "GET-AUTOM")}
                                            </Button>
                                        ) : selectedVersion.is_active_version ?
                                            (
                                                <Button
                                                    className='detail-autom-button active-autom'
                                                    variant="contained"
                                                    disabled={true}
                                                    onClick={(e) => {
                                                        return false;
                                                    }}
                                                >
                                                    {t("CONTENT.LIBRARIES.DETAIL.ALREADY-ACTIVATED")}
                                                </Button>
                                            ) : <Button
                                                className='detail-autom-button activate-autom'
                                                variant="contained"
                                                disabled={false}
                                                onClick={() => setActivateConfirm(true)}
                                            >
                                                {t("CONTENT.LIBRARIES.DETAIL.ACTIVATE")}
                                            </Button>
                                )
                            }
                        </div>
                        <div className='bot-tag-container'>
                            <div className="bot-tag">
                                <div>
                                    <div className="bot-desc-label">{t(label + 'APPLICATION')}</div>
                                    <div className="bot-desc-info">
                                        <ul className='application-names' >
                                            {
                                                bot.application_name.split(',').map((app_name, index) => (
                                                    <li key={index}>{app_name}</li>
                                                ))
                                            }
                                        </ul>
                                    </div>
                                </div>
                                <div>
                                    <div className="bot-desc-label">{t(label + 'PRICE')}</div>
                                    <div className="bot-desc-info">{t(label + 'FREE')}</div>
                                </div>
                                <div>
                                    <div className="bot-desc-label">{t(label + 'DEVELOPER')}</div>
                                    <div className="bot-desc-info">{botDetail?.user?.first_name + (botDetail?.user?.last_name ? botDetail?.user?.last_name : '')}</div>
                                </div>
                                <div>
                                    <div className="bot-desc-label">{t(label + 'DOWNLOAD-TIMES')}</div>
                                    <div className="bot-desc-info">{botDetail?.bot_details?.download_count}</div>
                                </div>
                            </div>
                            <div>
                                <div className="bot-desc-label">{t(label + 'SUPPORT')}</div>
                                <div className="bot-desc-info">{botDetail?.bot_details?.support}</div>
                            </div>
                            <div>
                                <div className="bot-desc-label">{t(label + 'LAST-UPDATE-DATE')}</div>
                                <div
                                    className="bot-desc-info">{convertToOnlyDateFormat(botDetail?.updated_at, personalization.date_format)}</div>
                            </div>
                            <div>
                                <div className="bot-desc-label">{t(label + 'DATE-OF-BIRTH')}</div>
                                <div
                                    className="bot-desc-info">{convertToOnlyDateFormat(botDetail?.created_at, personalization.date_format)}</div>
                            </div>
                            <div>
                                <div className="bot-desc-label">{t(label + 'TAGS')}</div>
                                <div className="bot-desc-info">
                                    <img src={tagIcon} className="tag-icon" />
                                    {/* <LocalOfferOutlined style={{
                                        width: 12,
                                        height: 12,
                                        opacity: 0.2,
                                        transform: 'rotate(90deg)',
                                        marginRight: 3
                                    }} /> */}
                                    {bot.bot_tags?.split(',').join(', ')}
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>}
            {
                openLibraryList &&
                <RequiredLibraries data={selectedVersion} handleClose={setOpenLibraryList} downloadBot={downloadBot} />
            }
            {
                downloadConfirm &&
                <Confirm setOpenDelete={setDownloadConfirm} message={t(label + 'GET-BOT-QUESTION')}
                    deleteFunc={() => setOpenLibraryList(true)} />
            }
            {
                activateConfirm &&
                <Confirm setOpenDelete={setActivateConfirm} message={t(label + 'CHANGE-ACTIVE-BOT-QUESTION')}
                    deleteFunc={changeActiveVersion} />
            }
            {
                openFullScreen && <DetailsFullScreen setOpenFullScreen={setOpenFullScreen} source={openFullScreen} />
            }

        </Dialog >
    );
}

export default Details;
